import { useCallback, useMemo, useState } from 'react';

import type {
  PrimaryFilterId,
  TreeSelection,
} from '../../../filters/types/filters';
import { TaxonomyIcicleDataProvider } from '../../DataProvider/composition/taxonomy-icicle/TaxonomyIcicleDataProvider';
import type { IcicleTaxonomySegment } from '../../DataProvider/composition/taxonomy-icicle/taxonomies/types';
import { useChart } from '../ChartProvider';
import type { DrilldownEntry } from '../utils/composition-drilldown';
import { IcicleContent } from './IcicleContent';
import type { IcicleDrillStep } from './icicle-data';
import { useIcicleChartKey, useIcicleChips } from './use-icicle-chips';

type TaxonomyIcicleProps = {
  segment: IcicleTaxonomySegment;
  primaryDimension: PrimaryFilterId;
};

const TaxonomyIcicleChart = ({
  segment,
  primaryDimension,
  chips,
}: TaxonomyIcicleProps & { chips: readonly TreeSelection[] }) => {
  const { item } = useChart();
  const [drilldownStack, setDrilldownStack] = useState<DrilldownEntry[]>([]);
  const drillEntry = drilldownStack[drilldownStack.length - 1];
  const drill = useMemo(
    () =>
      drillEntry === undefined
        ? null
        : {
            levelId: drillEntry.level,
            id: String(drillEntry.id),
            name: drillEntry.name,
          },
    [drillEntry]
  );

  const handleNavigate = useCallback((targetDepth: number) => {
    setDrilldownStack((prev) => prev.slice(0, targetDepth));
  }, []);

  const handleDrill = useCallback(
    (path: readonly IcicleDrillStep[], color: string) => {
      setDrilldownStack((prev) => [
        ...prev,
        ...path.map(
          (step): DrilldownEntry => ({
            level: step.levelId,
            id: step.id,
            name: step.name,
            segment,
            color,
          })
        ),
      ]);
    },
    [segment]
  );

  return (
    <TaxonomyIcicleDataProvider
      segment={segment}
      primaryDimension={primaryDimension}
      chips={chips}
      drill={drill}
      taxonomyCoverage={item?.plot?.treemap?.taxonomyCoverage}
    >
      {({ data, status }) => (
        <IcicleContent
          data={data}
          status={status}
          drilldownStack={drilldownStack}
          onDrill={handleDrill}
          onNavigate={handleNavigate}
          sizeTransform={item?.plot?.icicle?.sizeTransform}
        />
      )}
    </TaxonomyIcicleDataProvider>
  );
};

export const TaxonomyIcicle = ({
  segment,
  primaryDimension,
}: TaxonomyIcicleProps) => {
  const chips = useIcicleChips();
  const chartKey = useIcicleChartKey(segment, chips);
  if (chips.length === 0) return null;
  return (
    <TaxonomyIcicleChart
      key={chartKey}
      segment={segment}
      primaryDimension={primaryDimension}
      chips={chips}
    />
  );
};
